import Commons from "oc-commons/Commons.js";
import Scenes from "oc-threejs/Scenes.js";
import Maths from 'oc-commons/Maths';
import Connector from "./Connector";
import {v3, quat} from "oc-threejs/short3";

/**
 * Diverse Erweiterungen und Convenience-Methoden für Object3D, falls Connectoren verwendet werden
 */

const connectorSettings = () => STAGE.settings.connectors;

Commons.monkeyProperty(THREE.Object3D.prototype, 'isConnected', {
    get() {
        return this.getConnectors().some(c => c.connected);
    },
});

Commons.monkeyProperty(THREE.Object3D.prototype, 'needsConnectorConnection', {
    get() {
        return this._needsConnectorConnection === true;
    },
    set(v) {
        this._needsConnectorConnection = v && true;
    },
});

Commons.monkeyProperty(THREE.Object3D.prototype, 'rotateIfDragged', {
    get() {
        return this._rotateIfDragged !== false;
    },
    set(v) {
        this._rotateIfDragged = v && true;
    },
});

Commons.monkeyProperty(THREE.Object3D.prototype, 'aggregatePrio', {
    get() {
        return this._aggregatePrio || 0;
    },
    set(v) {
        this._aggregatePrio = v;
    },
});

Commons.monkeyProperty(THREE.Object3D.prototype, 'isRemoveOnDrop', {
    get() {
        return this._isRemoveOnDrop === true;
    },
    set(v) {
        this._isRemoveOnDrop = v && true;
    },
});

Commons.monkeyProperty(THREE.Object3D.prototype, 'isDropExchange', {
    get() {
        return this._isDropExchange === true;
    },
    set(v) {
        this._isDropExchange = v && true;
    },
});


/**
 * Erweitert THREE.Object3D.prototype um einen Satz von Methoden für Connectoren
 */
Commons.monkeyPatch(THREE.Object3D.prototype, {

    hasConnectionIfNeeded() {
        let check = false;
        if (this.needsConnectorConnection) {
            const forces = this.getConnectorForces();
            if (!Commons.isEmpty(forces)) {
                const dominating = forces[0];
                check = dominating.strength > 1 - Connector.EPSILON || dominating.distance < connectorSettings().snapDistance;
            }
        } else {
            check = true;
        }
        return check;
    },

    getConnectors() {
        return Scenes.filter(this, x => x instanceof Connector);
    },

    getAnchor(name = null) {
        return Scenes.find(this, x => x instanceof Connector && x.type.figure == 'Anchor' && (name == null || x.type.name == name));
    },

    allAnchorsAreGrounded() {
        const a = this.getAnchor();
        if (a) {
            return a.getAnchorPlane().distanceToPoint(a.getWorldPosition(v3())) < connectorSettings().anchorsAreGroundedDistance;
        }
        return false;
    },

    putAnchorOnGroundIfAny() {
        const anchor = this.getAnchor();
        if (anchor) {
            const plane = anchor.getAnchorPlane();
            const normal = plane.normal;
            const anchorUp = anchor.up.clone().applyQuaternion(anchor.getWorldQuaternion(quat()));
            // Orientierung
            if (normal.dot(anchorUp) < 1 - Connector.EPSILON) {
                console.log('orientate anchor to ground for ' + anchor);
                this.quaternion.multiply(new THREE.Quaternion().setFromUnitVectors(anchorUp, normal));
                this.updateMatrixWorld();
            }
            // Positionierung
            const anchorTarget = plane.projectPoint(anchor.getWorldPosition(v3()), v3());
            const offset = anchor.getWorldPosition(v3()).sub(anchorTarget);
            this.position.sub(offset);
        }
    },


    isPositionAllowed() {
        return this.allAnchorsAreGrounded() && this.hasConnectionIfNeeded();
    },


    getPairsWithScene() {
        const connectors = Scenes.filter(this, x => Connector.isConnectorNotAnchor(x));
        const otherConnectors = Scenes.filter(STAGE.scene, o => Connector.isConnectorNotAnchor(o) && !connectors.includes(o));
        const pairs = otherConnectors.reduce((pairs, sceneCon) => {
            pairs.push(...connectors.filter(handleCon => handleCon.matches(sceneCon)).map(handleCon => [sceneCon, handleCon]));
            return pairs;
        }, []);
        return pairs;
    },

    getConnectorForces() {

        const xo = this.getWorldPosition(v3());
        let forces = [];

        const fq0 = connectorSettings().distanceWhereForceIs0;
        const fq1 = connectorSettings().distanceWhereForceIs1;
        const dwfp0 = connectorSettings().distanceWhereForcePulseIs0;

        const openingAngleForce = connectorSettings().openingAngleForce;
        const angle0 = Maths.cutBetween(0, 360 - connectorSettings().openingAngle, 360) * Math.PI / 360 - 0.0001;
        const angle1 = Maths.cutBetween(0, 360 - connectorSettings().openingAngleWhereForceIs1, 360) * Math.PI / 360 + 0.0001;

        const pairs = this.getPairsWithScene();
        pairs.forEach(([sceneCon,handleCon])=>{
            if ( handleCon.isFreeForDrop() && sceneCon.isFreeForDrop()) {
                const xa = sceneCon.getWorldPosition(v3());
                const xb = handleCon.getWorldPosition(v3());
                const d = xa.distanceTo(xb);
                const forceFactor = sceneCon.type.strengthFactor * handleCon.type.strengthFactor * sceneCon.strengthFactor * handleCon.strengthFactor;

                const oo = Scenes.getOrientationVector(sceneCon);
                const oc = xb.clone().sub(xa);

                const angle = oo.angleTo(oc);
                const co = Scenes.getOrientationVector(handleCon);
                const connectorAngle = oo.angleTo(co);

                const strength = Connector.getConnectorForceUnbounded(d, fq0, fq1, forceFactor, connectorAngle, angle0, openingAngleForce, angle1);
                const pulseStrength = Connector.getConnectorForceUnbounded(d, dwfp0, fq1, forceFactor, connectorAngle, angle0, openingAngleForce, angle1);
                if (strength > 0 || d < dwfp0) {
                    // console.log(c.id+' '+o.id, strength, d, connectorAngle*180/Math.PI);
                    forces.push({
                        own: handleCon,
                        other: sceneCon,
                        distance: d,
                        angle: angle,
                        strength: strength,
                        pulseStrength: pulseStrength,
                    });
                }
            }
        });

        forces = _.orderBy(forces, m => m.strength, 'desc');

        // normalisiere die Kräfte, so dass das Maximum 1 nicht überschreitet

        const highestPulseStrength = _.max(forces.map(m => m.pulseStrength));

        if (highestPulseStrength > 1 - Connector.EPSILON) {
            //const pulseFactor = connectorSettings().PulseFactor;
            forces.forEach(m => m.pulseStrength = m.pulseStrength / highestPulseStrength);
        }

        //console.log(forces
        //    .sort((a,b)=>a.distance>b.distance)
        //    .map(a=>a.distance.toFixed(2)+' => '+a.strength.toFixed(2)).join(' ; '));

        const highestForce = _.max(forces.map(m => m.strength));
        if (highestForce > 1 - Connector.EPSILON) {
            forces = [forces[0]];
            forces[0].strength = 1;
        }
        //console.log(_.map(forces,m=>m.strength.toFixed(2)));

        return forces;
    },

    adjustCompetitiveConnectors() {
        const fq0 = connectorSettings().distanceWhereForceIs0;
        const fq1 = connectorSettings().distanceWhereForceIs1;
        const openingAngle = connectorSettings().openingAngle;
        const openingAngleForce = connectorSettings().openingAngleForce;
        const openingAngleWhereForceIs1 = connectorSettings().openingAngleWhereForceIs1;
        const dwfp0 = connectorSettings().distanceWhereForcePulseIs0;

        let connectors = this.getConnectors();
        for (let i = 0; i < connectors.length; i++) {
            for (let j = i + 1; j < connectors.length; j++) {
                if (connectors[i].isCompetitor(connectors[j])) {
                    let c = connectors[i];
                    let o = connectors[j];
                    const xa = o.getWorldPosition(v3());
                    const xb = c.getWorldPosition(v3());
                    const d = xa.distanceTo(xb);
                    const forceFactor = o.type.strengthFactor * c.type.strengthFactor * o.strengthFactor * c.strengthFactor;

                    const oo = Scenes.getOrientationVector(o);
                    const oc = xb.clone().sub(xa);

                    const angle = oo.angleTo(oc);
                    const co = Scenes.getOrientationVector(c);
                    const connectorAngle = oo.angleTo(co);

                    // const strength = Connector.getConnectorForceUnbounded(d,fq0,fq1,forceFactor,connectorAngle, openingAngle, openingAngleForce, openingAngleWhereForceIs1);
                    // const pulseStrength = Connector.getConnectorForceUnbounded(d,dwfp0,fq1,forceFactor, connectorAngle, openingAngle, openingAngleForce, openingAngleWhereForceIs1);

                    if (connectorAngle * 180 / Math.PI < 20) o.strengthFactor = Math.min(1, d / ((fq0 + fq1) / 2));
                }
            }
        }
    },


    // highestPrio 1 obj 1647_A_Seitenteil
    // connected 1647_A_Seitenteil/[Schaukelbalken,Eyelet](165)+ tprio 0 prio 0 highestPrio 1 unbreakable false
    // highestPrio 1 obj 1648_Schaukelbalken_2900
    // connected 1648_Schaukelbalken_2900/[Schaukelbalken,Hook](155)+ tprio 0 prio 0 highestPrio 1 unbreakable false
    // highestPrio 1 obj 1652_Grundturm
    // connected 1652_Grundturm/[schaukelbalkenhalter,Eyelet](73)+ tprio 0 prio 0 highestPrio 1 unbreakable true
    // highestPrio 1 obj 1668_Schaukelbalkenhalter
    groupOverAllConnectionsButStopAtAnchoredOnes() {
        const grouped = getAllConnectionsButStopAtAnchoredOnes.call(this);
        grouped.delete(this);
        Scenes.groupObjects(this, Array.from(grouped));
    },

    getAllConnectionsButStopAtAnchoredOnes() {
        const unbreakable = (connector) => connector.isUnbreakable || connector.connected.isUnbreakable;
        const aggregatePrio = (obj) => obj.aggregatePrio || (obj.getAnchor() ? 1 : 0);

        const unbreakablesMap = new Map();

        // gibt für das Objekt und alle daran transient hängenden unbreakables die aggregatePrio zurück
        function traverseUnbreakablesForPrio(obj, prioRef = [0]) {
            if (unbreakablesMap.has(obj)) {
                return unbreakablesMap.get(obj)[0];
            }
            unbreakablesMap.set(obj, prioRef);
            prioRef[0] = Math.max(prioRef[0], aggregatePrio(obj));
            obj.getConnectors().forEach(connector => {
                if (connector.connected && unbreakable(connector)) {
                    const parent = connector.connected.parent;
                    traverseUnbreakablesForPrio(parent, prioRef);
                }
            });
            return prioRef[0];
        }

        let highestPrio = 0;

        const grouped = new Set([this]);

        function traverseGrouped(obj) {
            highestPrio = Math.max(highestPrio, traverseUnbreakablesForPrio(obj));
            // console.log('highestPrio',highestPrio,'obj '+obj);
            obj.getConnectors().forEach(connector => {
                if (connector.connected) {
                    const parent = connector.connected.parent;
                    if (!grouped.has(parent)) {
                        // console.log('connected '+connector,'tprio',traverseUnbreakablesForPrio(parent),'prio',aggregatePrio(parent),'highestPrio',highestPrio,'unbreakable',unbreakable(connector));
                        if (highestPrio <= traverseUnbreakablesForPrio(parent) && !unbreakable(connector)) {
                            connector.disconnect();
                        } else {
                            grouped.add(parent);
                            traverseGrouped(parent);
                        }
                    }
                }
            });
        }

        traverseGrouped(this);
        return grouped;
    },


    breakAllConnections() {
        this.getConnectors().forEach(c => {
            if (c.connected) {
                c.disconnect()
            }
        });
    },

    createConnectorsAround(options, connectorCallback = (connector, properties) => {
    }) {

        options = Commons.defaultsDeepSafe({}, options, {
            // type grenzt ein, mit welchen anderen Connectoren sich der Connectoren verbindet.
            // der 1. Parameter ist der Name, der 2. die Art ("Hook","Eyelet","Magnet").
            // Nur Connectoren gleichen Namens verbinden sich, und auch nur Hook zu Eyelet und
            // Magnet auf alles.
            type: ["Default", "Magnet"],
            userData: {},
            //isRemoveOnDrop: false,
            // zusätzlicher Abstand vom center
            //offset: 0,
            // centrum des Polygons, auf dem die Connectoren liegen (ein 3d-vektor x,y,z wobei y die Höhe ist, Einheiten in cm)
            //center: Utils.vector.zero,
            // Abstand vom Mittelpunkt der Polygonseite
            //positionsPerSide: [0],
            // Wenn angegeben, dann werden Connectoren nur auf die hier als Index angegebene Polygonseiten gesetzt (Zählung entegegen Uhrzeigersinn, 0 ist vorne)
            //filterSideIndizes: [2],
            // die Seitennormale (richtung nach vorne)
            //sideZeroNormal: Utils.vector.z,
            // die Ebenennormale des Polygons (richtung nach oben)
            //planeNormal: Utils.vector.y,
            // der äußere Radius des Polygons
            //radius: 1,
            // der innere Radius des Polygons
            //innerRadius: 1,
            // die Anzahl der Seiten des Polygons
            //numberOfSides: 4,

        });

        return Scenes.createObjectsAroundObject(this, options, (p) => {
            const connector = new Connector(options.type);
            connector.userData = Commons.defaultsDeepSafe({}, options.userData);
            connector.setConnectorOptions(options);
            this.add(connector);
            connectorCallback(connector, p);
            return connector;
        });
    },

    createConnector(optionsOrType) {
        const options = (_.isArray(optionsOrType))

            ? {type: optionsOrType}

            : Commons.defaultsDeepSafe({}, optionsOrType, {
                type: ["Default", "Magnet"],
                //isRemoveOnDrop: false,
                //position: Utils.v(0,30,0),
                //quaternion: Utils.quaternion.zero,
            });

        const connector = new Connector(options.type);
        connector.setConnectorOptions(options);
        this.add(connector);
        this.adjustCompetitiveConnectors();
        return connector;
    },

    createAnchor(name = 'Ground') {
        return this.createConnector([name, 'Anchor']);
    },

});
